import React from 'react';
import { Container, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Columns, ChatLeft, Grid, Stars, BoxArrowUp } from 'react-bootstrap-icons';
import LandingHeader from '../components/LandingHeader';
import LandingFooter from '../components/LandingFooter';
import useScrollToTop from '../hooks/useScrollToTop';
import iconFigma from '../assets/icons/icon-figma.png';
import './Features.css';

const Features: React.FC = () => {
  const navigate = useNavigate();
  useScrollToTop();

  const handleTryNow = () => {
    navigate('/register');
  };

  return (
    <div className="features-page landing-page">
      <LandingHeader />

      <Container>
        <div className="page-header">
          <h1 className="page-title">Features: Your Co-Creative Ecosystem</h1>
          <p className="page-subtitle">
            Revealer AI is the <strong>intelligence beneath your intuition</strong>, built to turn the ambiguity of early discovery into a clear path forward. We provide the structural foundation for your "what-ifs," allowing you to move from raw data to real direction in minutes.
          </p>
          <p className="page-tagline">
            The work is the way. Stop staring at the blank screen and start with clarity.
          </p>
        </div>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <img src={iconFigma} alt="Figma" className="figma-icon" />
            </div>
            <h3>Seamless Figma Integration</h3>
            <p>
              Use Revealer AI to power your structural exploration, then move to Figma to polish your vision.
            </p>
            <ul>
              <li>Export your low-fi wireframes and artifacts directly into Figma</li>
              <li>Transition from raw discovery to polished interfaces</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <Columns size={40} />
            </div>
            <h3>Intelligent Project Management</h3>
            <p>
              The Projects page is more than just a storage space; it's your command center.
            </p>
            <ul>
              <li>Store conversations, research, and design artifacts in one place</li>
              <li>Centralize your discovery work</li>
              <li>Manage complex projects and shift to strategic reporting</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <ChatLeft size={40} />
            </div>
            <h3>Contextual Conversations</h3>
            <p>
              Never lose your thread. Discovery is a journey, not a single click.
            </p>
            <ul>
              <li>Keep a history of your recent interactions</li>
              <li>Dig deeper, your creative context is preserved</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <Grid size={40} />
            </div>
            <h3>AI-Powered Contextual Templates</h3>
            <p>
              Our templates ground your ideas in something solid, co-creating outcomes based on your specific goals.
            </p>
            <ul>
              <li>Intelligent Wireframes</li>
              <li>User Flows & Sitemaps</li>
              <li>Research Synthesis & Personas</li>
              <li>Transcript & Meeting Summaries</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <Stars size={40} />
            </div>
            <h3>Design Intelligence & Strategy</h3>
            <p>
              We're here to provide tools and meet you where you're at in your UX journey.
            </p>
            <ul>
              <li>Accessibility Compliance</li>
              <li>Competitive Analysis</li>
              <li>UX Roadmap</li>
              <li>Design Reviews</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <BoxArrowUp size={40} />
            </div>
            <h3>Raw Data to Team-Ready Assets</h3>
            <p>
              Turn fragmented research into cohesive concepts in minutes.
            </p>
            <ul>
              <li>Download as Figma files, JPEGs, PNGs, or SVGs to fit any workflow</li>
              <li>Transform raw files into visual assets that communicate the "why"</li>
            </ul>
          </div>
        </div>
      </Container>

      <section className="explore-section">
        <Container>
          <h2>Explore The Ecosystem</h2>
          <p>Get started with your first project.</p>
          <Button variant="primary" onClick={handleTryNow}>
            TRY REVEALER NOW <ArrowUpRight className="ms-2" />
          </Button>
        </Container>
      </section>

      <LandingFooter />
    </div>
  );
};

export default Features;
